import { useState, useEffect, useCallback } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useStoreModelDetail } from '@/hooks/useStoreModels'
import { useProcessingStatus } from '@/hooks/useProcessingStatus'
import { queryKeys } from '@/lib/queryKeys'
import { StoreModelDetailHeader } from '@/components/StoreModels/StoreModelDetailHeader'
import {
  StoreModelDetailTabs,
  type StoreModelTabValue,
} from '@/components/StoreModels/StoreModelDetailTabs'
import { StoreModelDetailTab } from '@/components/StoreModels/StoreModelDetailTab'
import { StoreModelUpdateTab } from '@/components/StoreModels/StoreModelUpdateTab'
import { StoreModelMembersTab } from '@/components/StoreModels/StoreModelMembersTab'
import { GroupsTab } from '@/components/StoreModels/GroupsTab'
import { ProcessingStatusCard } from '@/components/StoreModels/ProcessingStatusCard'
import { DetailSkeleton } from '@/components/common/DetailSkeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Info } from 'lucide-react'
import { toast } from '@/stores/useToastStore'
import type { AxiosError } from 'axios'
import type { ProcessingResponse } from '@/types'

export default function AdminStoreModelDetail() {
  const { modelId } = useParams<{ modelId: string }>()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { data: model, isLoading, error } = useStoreModelDetail(modelId)
  const [activeTab, setActiveTab] = useState<StoreModelTabValue>('detail')

  // Handle processing state — from router state (create flow) or from model data (reconnection)
  const [processingResponse, setProcessingResponse] =
    useState<ProcessingResponse | null>(
      (location.state as { processingResponse?: ProcessingResponse })
        ?.processingResponse ?? null
    )
  const processingStatus = useProcessingStatus(processingResponse)

  // Clear location state to prevent re-triggering on refresh
  useEffect(() => {
    if (location.state?.processingResponse) {
      window.history.replaceState({}, '')
    }
  }, [location.state])

  // Auto-connect to SSE when model is PROCESSING and we don't have a response yet
  const modelStatus = model?.status
  const modelProcessingId = model?.processing_id
  const modelEventsUrl = model?.events_url
  const modelId_ = model?.id
  useEffect(() => {
    if (
      modelStatus === 'PROCESSING' &&
      modelProcessingId &&
      modelEventsUrl &&
      modelId_ &&
      !processingResponse
    ) {
      setProcessingResponse({
        processing_id: modelProcessingId,
        resource_id: modelId_,
        status: 'PROCESSING',
        events_url: modelEventsUrl,
      })
    }
  }, [
    modelStatus,
    modelProcessingId,
    modelEventsUrl,
    modelId_,
    processingResponse,
  ])

  // When processing completes, refresh data and switch to grouping tab
  useEffect(() => {
    if (processingStatus.state === 'complete') {
      if (modelId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.storeModels.detail(modelId),
        })
      }
      toast.success('Processing complete')
      const timer = setTimeout(() => {
        setProcessingResponse(null)
        setActiveTab('groups')
      }, 1500)
      return () => clearTimeout(timer)
    }
    if (processingStatus.state === 'error') {
      toast.error(processingStatus.errorMessage ?? 'Processing failed')
      setProcessingResponse(null)
    }
  }, [
    processingStatus.state,
    processingStatus.errorMessage,
    modelId,
    queryClient,
  ])

  useEffect(() => {
    if (error) {
      const axiosError = error as AxiosError<{ detail?: string }>
      const message =
        axiosError.response?.data?.detail ?? 'Failed to load store model'
      toast.error(message)
    }
  }, [error])

  const handleUpdated = useCallback(() => {
    if (modelId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeModels.detail(modelId),
      })
    }
    setActiveTab('detail')
  }, [modelId, queryClient])

  const handleGroupingApplied = useCallback(() => {
    if (modelId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeModels.detail(modelId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeModels.groups(modelId),
      })
    }
  }, [modelId, queryClient])

  // Show processing screen while model is being created
  if (processingStatus.state === 'processing') {
    return (
      <div className="flex flex-col gap-6 p-6">
        {model && <StoreModelDetailHeader model={model} />}
        <ProcessingStatusCard status={processingStatus} />
      </div>
    )
  }

  if (isLoading || !model) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <DetailSkeleton fieldCount={12} badgeCount={2} showTabs />
      </div>
    )
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'detail':
        return <StoreModelDetailTab model={model} />
      case 'update':
        return <StoreModelUpdateTab model={model} onUpdated={handleUpdated} />
      case 'members':
        return <StoreModelMembersTab model={model} />
      case 'groups':
        return (
          <GroupsTab model={model} onGroupingApplied={handleGroupingApplied} />
        )
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <StoreModelDetailHeader model={model} />
      <Alert variant={model.plan_count > 0 ? 'info' : 'default'}>
        <Info className="h-4 w-4" />
        <AlertDescription>
          {model.plan_count > 0 ? (
            <>
              This model is used by{' '}
              <span className="font-semibold">{model.plan_count}</span>{' '}
              {model.plan_count === 1 ? 'plan' : 'plans'} across{' '}
              <span className="font-semibold">{model.line_item_count}</span>{' '}
              line {model.line_item_count === 1 ? 'item' : 'items'}. Changes
              might be disallowed.
            </>
          ) : (
            'This model is not currently used by any plans.'
          )}
        </AlertDescription>
      </Alert>
      <StoreModelDetailTabs activeTab={activeTab} onTabChange={setActiveTab} />
      {renderTabContent()}
    </div>
  )
}
